/**
 * 图片裁剪工具类
 * 提供图片自动裁剪、压缩、格式转换等功能
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

class ImageCropUtils {
    constructor() {
        // 默认配置
        this.defaultOptions = {
            width: 240,                    // 裁剪宽度
            height: 240,                   // 裁剪高度
            quality: 0.9,                  // 压缩质量 (0-1)
            x: 0,                          // 裁剪起始X坐标
            y: 0,                          // 裁剪起始Y坐标
            outputFormat: 'image/jpeg',    // 输出格式
            backgroundColor: '#FFFFFF'     // 背景填充颜色
        };
    }

    /**
     * 从File对象裁剪图片
     * @param {File} file - 图片文件对象
     * @param {Object} options - 裁剪配置选项
     * @returns {Promise<Object>} 返回包含原图和裁剪结果的对象
     */
    async cropFromFile(file, options = {}) {
        // 验证文件类型
        if (!file || !file.type.startsWith('image/')) {
            throw new Error('请提供有效的图片文件');
        }

        // 验证文件大小 (默认最大10MB)
        const maxSize = options.maxFileSize || 10 * 1024 * 1024;
        if (file.size > maxSize) {
            throw new Error(`文件大小超过限制 (${this.formatFileSize(maxSize)})`);
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = async () => {
                try {
                    const mergedOptions = { ...this.defaultOptions, ...options };
                    const croppedBlob = await this.cropImage(img, mergedOptions);
                    
                    resolve({
                        originalFile: file,
                        originalUrl: URL.createObjectURL(file),
                        croppedBlob: croppedBlob,
                        croppedUrl: URL.createObjectURL(croppedBlob),
                        originalSize: {
                            width: img.width,
                            height: img.height,
                            fileSize: file.size
                        },
                        croppedSize: {
                            width: mergedOptions.width,
                            height: mergedOptions.height,
                            fileSize: croppedBlob.size
                        },
                        compressionRatio: ((1 - croppedBlob.size / file.size) * 100).toFixed(1)
                    });
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => reject(new Error('图片加载失败'));
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * 从Image元素裁剪图片 (核心方法)
     * @param {HTMLImageElement} img - 图片元素
     * @param {Object} options - 裁剪配置选项
     * @returns {Promise<Blob>} 返回裁剪后的图片Blob
     */
    async cropImage(img, options = {}) {
        const config = { ...this.defaultOptions, ...options };
        const {
            width,
            height,
            quality,
            x,
            y,
            outputFormat,
            backgroundColor
        } = config;

        return new Promise((resolve, reject) => {
            try {
                // 创建画布
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // 设置画布尺寸
                canvas.width = width;
                canvas.height = height;

                // 设置背景颜色
                ctx.fillStyle = backgroundColor;
                ctx.fillRect(0, 0, width, height);

                // 计算实际裁剪区域（确保不超出原图边界）
                const sourceX = Math.max(0, Math.min(x, img.width - 1));
                const sourceY = Math.max(0, Math.min(y, img.height - 1));
                const sourceWidth = Math.min(width, img.width - sourceX);
                const sourceHeight = Math.min(height, img.height - sourceY);

                // 如果原图尺寸足够，直接裁剪
                if (sourceWidth > 0 && sourceHeight > 0) {
                    ctx.drawImage(
                        img,
                        sourceX, sourceY, sourceWidth, sourceHeight,
                        0, 0, sourceWidth, sourceHeight
                    );
                }

                // 转换为 Blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('图片裁剪失败'));
                    }
                }, outputFormat, quality);

            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * 批量裁剪不同尺寸
     * @param {File} file - 图片文件
     * @param {Array} sizeConfigs - 尺寸配置数组
     * @returns {Promise<Object>} 返回包含所有尺寸结果的对象
     */
    async batchCrop(file, sizeConfigs = []) {
        if (!sizeConfigs.length) {
            sizeConfigs = [
                { name: 'thumbnail', width: 100, height: 100 },
                { name: 'small', width: 240, height: 240 },
                { name: 'medium', width: 400, height: 400 },
                { name: 'large', width: 600, height: 600 }
            ];
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = async () => {
                try {
                    const results = {};
                    
                    for (const config of sizeConfigs) {
                        const options = { ...this.defaultOptions, ...config };
                        results[config.name] = await this.cropImage(img, options);
                    }
                    
                    resolve({
                        originalFile: file,
                        results: results,
                        originalSize: {
                            width: img.width,
                            height: img.height,
                            fileSize: file.size
                        }
                    });
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => reject(new Error('图片加载失败'));
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * 头像裁剪（正方形 + 圆形遮罩）
     * @param {File} file - 图片文件
     * @param {Object} options - 配置选项
     * @returns {Promise<Object>} 返回正方形和圆形两个版本
     */
    async cropAvatar(file, options = {}) {
        const config = {
            size: 200,
            quality: 0.9,
            x: 0,
            y: 0,
            ...options
        };

        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = async () => {
                try {
                    // 裁剪正方形
                    const squareBlob = await this.cropImage(img, {
                        width: config.size,
                        height: config.size,
                        quality: config.quality,
                        x: config.x,
                        y: config.y
                    });

                    // 创建圆形版本
                    const circularBlob = await this.createCircularImage(squareBlob, config.size);

                    resolve({
                        originalFile: file,
                        square: squareBlob,
                        circular: circularBlob,
                        squareUrl: URL.createObjectURL(squareBlob),
                        circularUrl: URL.createObjectURL(circularBlob)
                    });
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => reject(new Error('图片加载失败'));
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * 创建圆形图片
     * @param {Blob} imageBlob - 图片Blob
     * @param {number} size - 尺寸
     * @returns {Promise<Blob>} 圆形图片Blob
     */
    async createCircularImage(imageBlob, size) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = size;
                canvas.height = size;

                // 绘制圆形裁剪路径
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                ctx.clip();
                
                // 绘制图片
                ctx.drawImage(img, 0, 0, size, size);
                
                canvas.toBlob((blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('圆形图片创建失败'));
                    }
                }, 'image/png');
            };

            img.onerror = () => reject(new Error('图片加载失败'));
            img.src = URL.createObjectURL(imageBlob);
        });
    }

    /**
     * 智能裁剪（自动检测主要内容区域）
     * @param {File} file - 图片文件
     * @param {Object} options - 配置选项
     * @returns {Promise<Object>} 裁剪结果
     */
    async smartCrop(file, options = {}) {
        const config = {
            width: 240,
            height: 240,
            quality: 0.9,
            ...options
        };

        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = async () => {
                try {
                    // 简单的智能裁剪逻辑：优先从中心区域裁剪
                    const centerX = Math.max(0, (img.width - config.width) / 2);
                    const centerY = Math.max(0, (img.height - config.height) / 2);

                    const croppedBlob = await this.cropImage(img, {
                        ...config,
                        x: centerX,
                        y: centerY
                    });

                    resolve({
                        originalFile: file,
                        croppedBlob: croppedBlob,
                        croppedUrl: URL.createObjectURL(croppedBlob),
                        cropPosition: { x: centerX, y: centerY }
                    });
                } catch (error) {
                    reject(error);
                }
            };

            img.onerror = () => reject(new Error('图片加载失败'));
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * 格式化文件大小
     * @param {number} bytes - 字节数
     * @returns {string} 格式化后的文件大小
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * 获取图片信息
     * @param {File} file - 图片文件
     * @returns {Promise<Object>} 图片信息
     */
    async getImageInfo(file) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            
            img.onload = () => {
                resolve({
                    width: img.width,
                    height: img.height,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    lastModified: file.lastModified,
                    aspectRatio: (img.width / img.height).toFixed(2)
                });
            };

            img.onerror = () => reject(new Error('图片加载失败'));
            img.src = URL.createObjectURL(file);
        });
    }

    /**
     * 下载Blob为文件
     * @param {Blob} blob - 要下载的Blob
     * @param {string} fileName - 文件名
     */
    downloadBlob(blob, fileName = 'cropped_image.jpg') {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }
}

// 创建全局实例
const imageCropUtils = new ImageCropUtils();

// 导出类和实例（支持多种导入方式）
if (typeof module !== 'undefined' && module.exports) {
    // Node.js/CommonJS
    module.exports = { ImageCropUtils, imageCropUtils };
} else if (typeof define === 'function' && define.amd) {
    // AMD
    define([], function() {
        return { ImageCropUtils, imageCropUtils };
    });
} else {
    // 浏览器全局变量
    window.ImageCropUtils = ImageCropUtils;
    window.imageCropUtils = imageCropUtils;
}
